import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../Utils/Axios';
import { createHospitalJob, fetchHospitalJobs } from '../../store/hospitalSlice';
import SearchableDropdown from '../../Components/SearchableDropdown';
import { useRegion } from '../../context/RegionContext';
import {
  getRegionCountryOptions,
  getRegionCountryRecord,
  getRegionCityOptions,
  getRegionStateLabel,
  getRegionStateOptions,
  getRegionStateRecord,
} from '../../Utils/region';

const initialForm = {
  title: '',
  yourId: '',
  eligibleToWorkUSA: null,
  category: '',
  subcategory: '',
  specialty: '',
  department: '',
  vacancies: '',
  country: '',
  state: '',
  city: '',
  zip: '',
  employmentType: 'Full-time',
  salaryMode: 'fixed',
  fixedSalary: '',
  salaryMin: '',
  salaryMax: '',
  experienceLevel: '',
  jobTags: '',
  deadline: '',
  description: '',
  requirements: '',
  acceptedTerms: false,
};

const PostJob = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status } = useSelector((state) => state.hospital);
  const [form, setForm] = useState(initialForm);
  const brandYellow = '#c99a18';
  const { region } = useRegion();
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [pricing, setPricing] = useState({
    trialDays: 30,
    pricePerDayUSD: 2.5,
    pricePerMonthUSD: 75,
  });
  const [trial, setTrial] = useState({ isTrialActive: true, remainingDays: 90 });
  const [requiresPlanSelection, setRequiresPlanSelection] = useState(false);
  const [paymentApproved, setPaymentApproved] = useState(false);

  const stateLabel = useMemo(() => getRegionStateLabel(region), [region]);
  const eligibilityLabel = useMemo(
    () => (region === 'us' ? 'Are you eligible to work in USA?*' : 'Are you eligible to work in this country?*'),
    [region]
  );
  const countryOptions = useMemo(() => (region === 'af' ? getRegionCountryOptions(region) : []), [region]);
  const selectedCountry = useMemo(
    () => (region === 'af' ? getRegionCountryRecord(region, form.country) : null),
    [region, form.country]
  );
  const selectedStateObj = useMemo(
    () => getRegionStateRecord(region, form.state, selectedCountry),
    [region, form.state, selectedCountry]
  );
  const cities = useMemo(
    () => getRegionCityOptions(region, selectedStateObj, selectedCountry),
    [region, selectedStateObj, selectedCountry]
  );
  const stateNames = useMemo(() => getRegionStateOptions(region, selectedCountry), [region, selectedCountry]);
  const cityNames = useMemo(() => cities, [cities]);
  const filteredSubcategories = useMemo(() => {
    if (!form.category) return [];
    const filtered = subcategories.filter(
      (item) => String(item.categoryName || '').trim().toLowerCase() === String(form.category).trim().toLowerCase()
    );
    const other = [];
    const rest = [];
    filtered.forEach((item) => {
      if (String(item.name || '').trim().toLowerCase() === 'other') {
        other.push(item);
      } else {
        rest.push(item);
      }
    });
    return [...rest, ...other];
  }, [form.category, subcategories]);
  const filteredSpecialties = useMemo(() => {
    if (!form.subcategory) return [];
    return specialties.filter(
      (item) =>
        String(item.subcategoryName || '').trim().toLowerCase() ===
        String(form.subcategory).trim().toLowerCase()
    );
  }, [form.subcategory, specialties]);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [categoryRes, subcategoryRes, specialtyRes, configRes] = await Promise.all([
          api.get('/api/categories'),
          api.get('/api/categories/subcategories'),
          api.get('/api/categories/specialties'),
          api.get('/api/hospital/posting-config'),
        ]);

        setCategories(categoryRes.data?.categories || []);
        setSubcategories(subcategoryRes.data?.subcategories || []);
        setSpecialties(specialtyRes.data?.specialties || []);
        setPricing(
          configRes.data?.pricing || { trialDays: 30, pricePerDayUSD: 2.5, pricePerMonthUSD: 75 }
        );
        setTrial(configRes.data?.trial || { isTrialActive: true, remainingDays: 90 });
        setRequiresPlanSelection(Boolean(configRes.data?.requiresPlanSelection));
        setPaymentApproved(sessionStorage.getItem('hpwp_post_payment_confirmed') === '1');
      } catch (error) {
        toast.error('Failed to load posting settings');
      }
    };

    loadMeta();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValueRaw = type === 'checkbox' ? checked : value;
    const nextValue =
      name === 'zip' && typeof nextValueRaw === 'string'
        ? nextValueRaw.replace(/\D/g, '')
        : nextValueRaw;

    if (name === 'country') {
      setForm((prev) => ({ ...prev, country: value, state: '', city: '' }));
      return;
    }
    if (name === 'state') {
      setForm((prev) => ({ ...prev, state: value, city: '' }));
      return;
    }
    if (name === 'category') {
      setForm((prev) => ({ ...prev, category: value, subcategory: '', specialty: '' }));
      return;
    }
    if (name === 'subcategory') {
      setForm((prev) => ({ ...prev, subcategory: value, specialty: '' }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: nextValue }));
  };

  const postJob = async (payload) => {
    const createdJob = await dispatch(createHospitalJob(payload)).unwrap();
    dispatch(fetchHospitalJobs());
    return createdJob;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.acceptedTerms) {
      toast.error('Please accept terms and conditions');
      return;
    }
    if (form.eligibleToWorkUSA !== true) {
      toast.error(
        region === 'us'
          ? 'Please select "Yes" for eligibility to work in USA'
          : 'Please select "Yes" for eligibility to work in this country'
      );
      return;
    }
    if (region === 'af' && !form.country) {
      toast.error('Please select country');
      return;
    }
    if (!form.state || !form.city) {
      toast.error(`Please select ${stateLabel.toLowerCase()} and city`);
      return;
    }
    if (requiresPlanSelection) {
      toast.info('Please select your plan first.');
      navigate('/hospital-pricing?select=1&return=/post-job');
      return;
    }

    if (form.zip && !/^\d+$/.test(form.zip)) {
      toast.error('ZIP code must contain only numbers');
      return;
    }

    const salaryMinValue =
      form.salaryMode === 'fixed'
        ? form.fixedSalary
        : form.salaryMode === 'range'
          ? form.salaryMin
          : '';

    const salaryMaxValue =
      form.salaryMode === 'fixed'
        ? form.fixedSalary
        : form.salaryMode === 'range'
          ? form.salaryMax
          : '';

    const salaryMinNumber = salaryMinValue ? Number(salaryMinValue) : undefined;
    const salaryMaxNumber = salaryMaxValue ? Number(salaryMaxValue) : undefined;

    if (
      form.salaryMode === 'range' &&
      salaryMinNumber &&
      salaryMaxNumber &&
      salaryMinNumber > salaryMaxNumber
    ) {
      toast.error('Minimum salary cannot be greater than maximum salary');
      return;
    }

    const requirementsParts = [
      form.requirements?.trim(),
      form.experienceLevel ? `Experience Level: ${form.experienceLevel}` : '',
      form.vacancies ? `Vacancies: ${form.vacancies}` : '',
      form.jobTags ? `Job Tags: ${form.jobTags}` : '',
    ].filter(Boolean);

    const payload = {
      ...form,
      location:
        region === 'af'
          ? [form.city, form.state, form.country].filter(Boolean).join(', ')
          : [form.city, form.state].filter(Boolean).join(', '),
      country: region === 'af' ? form.country : undefined,
      salaryMin: salaryMinNumber,
      salaryMax: salaryMaxNumber,
      eligibleToWorkUSA: Boolean(form.eligibleToWorkUSA),
      deadline: form.deadline || undefined,
      requirements: requirementsParts.join('\n'),
      paymentConfirmed: paymentApproved && !trial?.isTrialActive,
    };

    delete payload.salaryMode;
    delete payload.fixedSalary;
    delete payload.experienceLevel;
    delete payload.jobTags;
    delete payload.vacancies;
    delete payload.acceptedTerms;

    try {
      const createdJob = await postJob(payload);
      if (createdJob?.billingCharge?.charged) {
        const cycleLabel = createdJob.billingCharge.cycle === 'monthly' ? 'month' : 'day';
        toast.info(`Premium plan: $${createdJob.billingCharge.amountUSD} charged for this ${cycleLabel}.`);
      }
      toast.success('Hospital job posted successfully');
      setForm(initialForm);
      sessionStorage.removeItem('hpwp_post_payment_confirmed');
      setPaymentApproved(false);

      const configRes = await api.get('/api/hospital/posting-config');
      setTrial(configRes.data?.trial || trial);
      setRequiresPlanSelection(Boolean(configRes.data?.requiresPlanSelection));
    } catch (err) {
      if (err?.status === 428 || err?.code === 'PLAN_SELECTION_REQUIRED') {
        toast.info('Please select your plan first.');
        navigate('/hospital-pricing?select=1&return=/post-job');
        return;
      }

      if (err?.status === 402) {
        const amount = err?.amountDueUSD || pricing.pricePerDayUSD;
        toast.info(
          `Free trial expired. Please choose Premium ($${amount}/day or $${pricing.pricePerMonthUSD}/month).`
        );
        navigate('/hospital-pricing?expired=1&return=/post-job');
        return;
      }

      toast.error(err?.message || 'Failed to post hospital job');
    }
  };

  return (
    <div className="job-post-area pt-120 mb-120">
      <div className="container">
        <div className="row">
          <div className="col-lg-12">
            <div className="form-wrapper">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-lg-12">
                    <div className="section-title2">
                      <h5>Job Information:</h5>
                      <p style={{ marginTop: 10 }}>
                        {trial?.isTrialActive
                          ? `Free trial active (${trial?.remainingDays || 0} day(s) left).`
                          : `Trial ended. Choose Premium ($${pricing?.pricePerDayUSD || 2.5}/day or $${pricing?.pricePerMonthUSD || 75}/month).`}
                      </p>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-inner mb-25">
                      <label htmlFor="jobtitle">Job Title*</label>
                      <div className="input-area">
                        <img src="/assets/images/icon/company-2.svg" alt="" />
                        <input
                          type="text"
                          id="jobtitle"
                          name="title"
                          value={form.title}
                          onChange={handleChange}
                          placeholder="Registered Nurse - ICU"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-inner mb-25">
                      <label htmlFor="yourId">Enter Your ID*</label>
                      <div className="input-area">
                        <img src="/assets/images/icon/user-2.svg" alt="" />
                        <input
                          type="text"
                          id="yourId"
                          name="yourId"
                          value={form.yourId}
                          onChange={handleChange}
                          placeholder="e.g. HPWP-EMP-1024"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-inner mb-25">
                      <label>Job Category*</label>
                      <div className="input-area">
                        <img src="/assets/images/icon/category-2.svg" alt="" />
                        <select name="category" value={form.category} onChange={handleChange} className="select1">
                          <option value="">Select category</option>
                          {categories.map((item) => (
                            <option key={item._id} value={item.name}>
                              {item.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-inner mb-25">
                      <label>Job Subcategory</label>
                      <div className="input-area">
                        <img src="/assets/images/icon/category-2.svg" alt="" />
                        <select
                          name="subcategory"
                          value={form.subcategory}
                          onChange={handleChange}
                          className="select1"
                          disabled={!form.category}
                        >
                          <option value="">Select subcategory</option>
                          {filteredSubcategories.map((item) => (
                            <option key={item._id} value={item.name}>
                              {item.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-inner mb-25">
                      <label>Job Specialty</label>
                      <div className="input-area">
                        <img src="/assets/images/icon/category-2.svg" alt="" />
                        <select
                          name="specialty"
                          value={form.specialty}
                          onChange={handleChange}
                          className="select1"
                          disabled={!form.subcategory}
                        >
                          <option value="">Select specialty</option>
                          {filteredSpecialties.map((item) => (
                            <option key={item._id} value={item.name}>
                              {item.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-inner mb-25">
                      <label htmlFor="vacancies">Vacancies*</label>
                      <div className="input-area">
                        <img src="/assets/images/icon/user-2.svg" alt="" />
                        <input
                          type="number"
                          id="vacancies"
                          name="vacancies"
                          min="1"
                          value={form.vacancies}
                          onChange={handleChange}
                          placeholder="e.g. 7"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-inner mb-25">
                      <label htmlFor="department">Department</label>
                      <div className="input-area">
                        <img src="/assets/images/icon/company-2.svg" alt="" />
                        <input
                          type="text"
                          id="department"
                          name="department"
                          value={form.department}
                          onChange={handleChange}
                          placeholder="Emergency / ICU / Radiology"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-inner">
                      <div className="salary-wrap">
                        <label className="label">Budget/Salary*</label>
                        <div className="salery-select-area">
                          <div className="single-salery">
                            <input
                              className="form-check-input"
                              style={{ accentColor: brandYellow }}
                              type="radio"
                              id="fixedPrice1"
                              name="salaryMode"
                              value="fixed"
                              checked={form.salaryMode === 'fixed'}
                              onChange={handleChange}
                            />
                            <label htmlFor="fixedPrice1">Fixed Salary</label>
                            <br />
                          </div>
                          <div className="single-salery">
                            <input
                              className="form-check-input"
                              style={{ accentColor: brandYellow }}
                              type="radio"
                              id="rangePrice1"
                              name="salaryMode"
                              value="range"
                              checked={form.salaryMode === 'range'}
                              onChange={handleChange}
                            />
                            <label htmlFor="rangePrice1">Salary Range</label>
                            <br />
                          </div>
                          <div className="single-salery">
                            <input
                              className="form-check-input"
                              style={{ accentColor: brandYellow }}
                              type="radio"
                              id="negotiable1"
                              name="salaryMode"
                              value="negotiable"
                              checked={form.salaryMode === 'negotiable'}
                              onChange={handleChange}
                            />
                            <label htmlFor="negotiable1">Negotiable</label>
                            <br />
                          </div>
                        </div>
                      </div>

                      {form.salaryMode === 'fixed' && (
                        <div className="row">
                          <div className="col-lg-12">
                            <div className="input-area mb-25" id="fixedPrice">
                              <img src="/assets/images/icon/salary-2.svg" alt="" />
                              <input
                                type="number"
                                name="fixedSalary"
                                value={form.fixedSalary}
                                onChange={handleChange}
                                placeholder="Salary"
                                required
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {form.salaryMode === 'range' && (
                        <div className="row" id="rangePrice">
                          <div className="col-lg-6 mb-25">
                            <div className="input-area">
                              <img src="/assets/images/icon/salary-2.svg" alt="" />
                              <input
                                type="number"
                                name="salaryMin"
                                value={form.salaryMin}
                                onChange={handleChange}
                                placeholder="Min Salary"
                                required
                              />
                            </div>
                          </div>
                          <div className="col-lg-6">
                            <div className="input-area">
                              <img src="/assets/images/icon/salary-2.svg" alt="" />
                              <input
                                type="number"
                                name="salaryMax"
                                value={form.salaryMax}
                                onChange={handleChange}
                                placeholder="Max Salary"
                                required
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-inner mb-25">
                      <label>Job Type*</label>
                      <div className="input-area">
                        <img src="/assets/images/icon/company-2.svg" alt="" />
                        <select
                          name="employmentType"
                          value={form.employmentType}
                          onChange={handleChange}
                          className="select1"
                        >
                          <option value="Full-time">Full Time</option>
                          <option value="Part-time">Part Time</option>
                          <option value="Contract">Contract</option>
                          <option value="Internship">Internship</option>
                          <option value="Per diem">Per Diem</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-inner mb-25">
                      <label>{eligibilityLabel}</label>
                      <div className="form-agreement form-inner d-flex justify-content-between flex-wrap">
                        <div className="form-group two" style={{ marginRight: 18 }}>
                          <input
                            type="checkbox"
                            style={{ accentColor: brandYellow }}
                            id="eligibleYes"
                            checked={form.eligibleToWorkUSA === true}
                            onChange={() => setForm((prev) => ({ ...prev, eligibleToWorkUSA: true }))}
                          />
                          <label htmlFor="eligibleYes">Yes</label>
                        </div>
                        <div className="form-group two">
                          <input
                            type="checkbox"
                            style={{ accentColor: brandYellow }}
                            id="eligibleNo"
                            checked={form.eligibleToWorkUSA === false}
                            onChange={() => setForm((prev) => ({ ...prev, eligibleToWorkUSA: false }))}
                          />
                          <label htmlFor="eligibleNo">No</label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-inner mb-25">
                      <label htmlFor="experiences">Experience Level*</label>
                      <div className="input-area">
                        <img src="/assets/images/icon/email-2.svg" alt="" />
                        <input
                          type="text"
                          id="experiences"
                          name="experienceLevel"
                          value={form.experienceLevel}
                          onChange={handleChange}
                          placeholder="e.g. 2+ years in ICU"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {region === 'af' ? (
                    <div className="col-md-6">
                      <div className="form-inner mb-25">
                        <label>Country*</label>
                        <SearchableDropdown
                          options={countryOptions}
                          placeholder="Select country"
                          value={form.country}
                          onChange={(val) => setForm((prev) => ({ ...prev, country: val, state: '', city: '' }))}
                        />
                      </div>
                    </div>
                  ) : null}
                  <div className="col-md-6">
                    <div className="form-inner mb-25" style={{ position: 'relative' }}>
                      <label>{stateLabel}*</label>
                      <select
                        value={form.state}
                        onChange={(e) => setForm((prev) => ({ ...prev, state: e.target.value, city: '' }))}
                        disabled={region === 'af' && !form.country}
                        style={{
                          width: '100%',
                          height: '60px',
                          padding: '0 50px 0 20px',
                          background: '#fff',
                          border: '1px solid #DFE9E5',
                          borderRadius: '5px',
                          color: form.state ? 'var(--title-color1)' : '#7C8A9B',
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          opacity: region === 'af' && !form.country ? 0.6 : 1,
                          cursor: region === 'af' && !form.country ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <option value="">{stateLabel}</option>
                        {stateNames.map((state) => (
                          <option key={state} value={state}>
                            {state}
                          </option>
                        ))}
                      </select>
                      <span
                        style={{
                          position: 'absolute',
                          right: '25px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none',
                        }}
                      >
                        <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M1.41 0.589966L6 5.16997L10.59 0.589966L12 1.99997L6 7.99997L0 1.99997L1.41 0.589966Z"
                            fill="#1C2120"
                          />
                        </svg>
                      </span>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-inner mb-25" style={{ position: 'relative' }}>
                      <label>City*</label>
                      <select
                        value={form.city}
                        onChange={(e) => setForm((prev) => ({ ...prev, city: e.target.value }))}
                        disabled={!form.state}
                        style={{
                          width: '100%',
                          height: '60px',
                          padding: '0 50px 0 20px',
                          background: '#fff',
                          border: '1px solid #DFE9E5',
                          borderRadius: '5px',
                          color: form.city ? 'var(--title-color1)' : '#7C8A9B',
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          opacity: !form.state ? 0.6 : 1,
                          cursor: !form.state ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <option value="">City</option>
                        {cityNames.map((city) => (
                          <option key={city} value={city}>
                            {city}
                          </option>
                        ))}
                      </select>
                      <span
                        style={{
                          position: 'absolute',
                          right: '25px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none',
                        }}
                      >
                        <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M1.41 0.589966L6 5.16997L10.59 0.589966L12 1.99997L6 7.99997L0 1.99997L1.41 0.589966Z"
                            fill="#1C2120"
                          />
                        </svg>
                      </span>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-inner mb-25">
                      <label htmlFor="zip">ZIP Code*</label>
                      <div className="input-area">
                        <img src="/assets/images/icon/location.svg" alt="" />
                        <input
                          type="text"
                          id="zip"
                          name="zip"
                          value={form.zip}
                          onChange={handleChange}
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={10}
                          placeholder="e.g. 10001"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-inner select-jobtag mb-25">
                      <label>Job Tag*</label>
                      <div className="input-area">
                        <img src="/assets/images/icon/search-2.svg" alt="" />
                        <input
                          type="text"
                          name="jobTags"
                          value={form.jobTags}
                          onChange={handleChange}
                          placeholder="e.g. ICU, Night Shift, RN"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-inner mb-25">
                      <label htmlFor="deadline">Deadline*</label>
                      <div className="input-area">
                        <img src="/assets/images/icon/calender2.svg" alt="" />
                        <input
                          type="date"
                          id="deadline"
                          name="deadline"
                          value={form.deadline}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-md-12">
                    <div className="form-inner mb-30">
                      <label htmlFor="description">Job Description*</label>
                      <textarea
                        id="description"
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        placeholder="Responsibilities, shifts, and role scope."
                        rows={6}
                        required
                      />
                    </div>
                  </div>

                  <div className="col-md-12">
                    <div className="form-inner mb-30">
                      <label htmlFor="requirements">Requirements*</label>
                      <textarea
                        id="requirements"
                        name="requirements"
                        value={form.requirements}
                        onChange={handleChange}
                        placeholder="License, certifications, and other requirements."
                        rows={5}
                        required
                      />
                    </div>
                  </div>

                  <div className="col-md-12">
                    <div className="form-agreement form-inner d-flex justify-content-between flex-wrap">
                      <div className="form-group two">
                        <input
                          type="checkbox"
                          style={{ accentColor: brandYellow }}
                          id="post_terms"
                          name="acceptedTerms"
                          checked={form.acceptedTerms}
                          onChange={handleChange}
                        />
                        <label htmlFor="post_terms">
                          Here, I accepted hospital terms &amp; conditions.
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-12">
                    <div className="form-inner">
                      <button className="primry-btn-2 lg-btn w-unset" type="submit" disabled={status === 'loading'}>
                        {status === 'loading' ? 'Posting...' : 'Post Now'}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostJob;
